import { v4 } from "uuid";

import { getDefaultJsonSchemaAiNodeBE } from "src/aiNode/defaultValues";
import {
  AiNodeV2Form,
  BasicModelCard,
  AiNodeJsonSchemaUIT,
  AiNodeJsonSchemaBET,
  AiNodeJsonSchemaPropertyUIT,
} from "src/aiNode/types";
import { AiNodeV2Meta } from "src/clients/flow-api";
import { SchemaOptions } from "src/router/SearchParams";
import { SchemaConverter } from "src/schema/utils";

// The "text" output and insights which we don't want to cleanup
// when processing JSON schemas
const PROTECTED_RESPONSE_NAMES = ["default", "provider_response_time"];

export const getDefaultAiNodeModelId = (
  availableModels: BasicModelCard[],
): string => availableModels[0].model_id;

export const getAiNodeNewProperty = (): AiNodeJsonSchemaPropertyUIT => {
  return {
    fieldName: "",
    type: ["string", "null"],
    timestamp: Date.now().toString(),
    required: true,
    sensitive: false,
    outputId: v4(),
    description: "",
  };
};

export const convertAiNodeJsonSchemaBETtoUIT = (
  schemaBE: AiNodeJsonSchemaBET,
): AiNodeJsonSchemaUIT => {
  const schemaUI = SchemaConverter.beToUI(schemaBE, SchemaOptions.Output);

  return {
    ...schemaUI,
    properties: schemaUI.properties.map((property) => ({
      ...property,
      description: schemaBE.properties[property.fieldName].description ?? "",
      // Backfilling the outputId in case it's missing so we can regenerate
      // the response fields down the line
      outputId: schemaBE.properties[property.fieldName].outputId ?? v4(),
    })),
  };
};

export const convertAiNodeJsonSchemaUITtoBET = (
  schemaUI: AiNodeJsonSchemaUIT,
): AiNodeJsonSchemaBET => {
  const schemaBE = SchemaConverter.uiToBE(schemaUI, SchemaOptions.Output);
  const propertiesMappingUI = Object.fromEntries(
    schemaUI.properties.map((property) => [
      property.fieldName,
      {
        outputId: property.outputId,
        description: property.description,
      },
    ]),
  );
  return {
    ...schemaBE,
    properties: Object.fromEntries(
      Object.entries(schemaBE.properties).map(([fieldName, property]) => [
        fieldName,
        {
          ...property,
          outputId: propertiesMappingUI[fieldName].outputId,
          description: propertiesMappingUI[fieldName]?.description,
        },
      ]),
    ),
  };
};

export const convertAiNodeV2FormToBE = (form: AiNodeV2Form): AiNodeV2Meta => {
  const uiSchema = form.inference.response_format.json_schema;
  if (!uiSchema) {
    return {
      ...form,
      inference: {
        ...form.inference,
        response_format: {
          ...form.inference.response_format,
          json_schema: undefined, // TODO: return default?
        },
      },
    };
  }

  // JSONSchema is a source of truth for the response fields, therefore
  // we re-create the response object based on the schemas

  const backendSchema = convertAiNodeJsonSchemaUITtoBET(uiSchema);
  // Keep protected response field names (text output and insights)
  const response = Object.fromEntries(
    Object.entries(form.response).filter(([key]) =>
      PROTECTED_RESPONSE_NAMES.includes(key),
    ),
  );

  // Add output fields from schema
  Object.entries(backendSchema.properties).forEach(([fieldName, property]) => {
    response[fieldName] = {
      // We are backfilling the `outputId` if it's missing when parsing BE
      // data, so `outputId` will always be set at this point
      id: property.outputId!,
      active: true,
      mapped_to: fieldName,
    };
  });

  return {
    ...form,
    response,
    inference: {
      ...form.inference,
      response_format: {
        ...form.inference.response_format,
        json_schema: backendSchema,
      },
    },
  };
};

export const convertAiNodeV2BEToForm = (data: AiNodeV2Meta): AiNodeV2Form => {
  const backendSchema = (data.inference.response_format.json_schema ??
    getDefaultJsonSchemaAiNodeBE()) as AiNodeJsonSchemaBET;

  const uiSchema = convertAiNodeJsonSchemaBETtoUIT(backendSchema);

  return {
    model: data.model,
    prompts: data.prompts,
    response: data.response, // TODO: backfill responses?
    inference: {
      ...data.inference,
      response_format: {
        ...data.inference.response_format,
        json_schema: uiSchema,
      },
    },
    config: data.config,
  };
};
